import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS, type Transform } from "@dnd-kit/utilities";
import {
  type CompetitorListing,
  type ContentGapMatch,
  type ContentGapMissing,
  type ContentGapResult,
  type ContentGapOverride,
  type EntityOverlapResult,
  type EntityOverlapRow,
  type OverlapVerdict,
  deleteContentGapOverride,
  formatErrorForAlert,
  getContentGap,
  getContentGapOverrides,
  getEntityOverlap,
  hideContentGapService,
  importMissingToBuildQueue,
  runEntityOverlap,
  setContentGapOverride,
  updateClient,
} from "../lib/api";
import { Button, Field, TextInput } from "../components/Form";
import { useToast } from "../components/Toast";
import type { WorkspaceContext } from "./ClientWorkspacePage";

// =========================================================================
// Top-level types for Content Gap rendering — defined at module scope so
// useMemo's inferred return type is stable and child components share the
// same row-shape definition.
// =========================================================================
type ContentGapRowState =
  | {
      state: "matched";
      matchData: ContentGapMatch & { isOverride?: boolean };
    }
  | { state: "missing" };

type ContentGapGroup = {
  category: string;
  /** null = this category isn't in the gap universe (no category-level
   *  scoring). It still has services under it that we want to render. */
  categoryRow: ContentGapRowState | null;
  services: Array<{
    name: string;
    row: ContentGapRowState;
    /** True when user marked this service hidden via the × button. Hidden
     *  services don't count toward coverage and only render when the
     *  "Show hidden" toggle is on. */
    hidden: boolean;
  }>;
};

/**
 * Research tab — three cards stacked.
 *
 *   1. Content Gap Analysis — derived from existing crawl_pages + GBP
 *      universe. No external APIs. Has Import-to-Content-Generation.
 *
 *   2. GBP Profile Data — read-only summary of the client's GBP setup.
 *      Pulled from the client record.
 *
 *   3. Service Entity Overlap Research — runs SERP overlap analysis to
 *      determine which services Google treats as distinct entities. Stubbed
 *      for Hamblin to match the reference numbers; real DataForSEO later.
 */
export default function ResearchTab() {
  const { client, refreshClient } = useOutletContext<WorkspaceContext>();
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      {/* Page header — in a white card, not on the grey background */}
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-bg-subtle border border-border flex items-center justify-center text-text-muted shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </div>
        <div>
          <h1 className="text-text font-semibold text-xl">Gap Analysis</h1>
          <p className="text-text-muted text-sm mt-0.5">
            Find which services in your Google Business Profile don't have a
            dedicated page on your site yet. Add the missing ones to the writing
            queue.
          </p>
        </div>
      </div>

      <ContentGapCard
        client={client}
        navigate={navigate}
        refreshClient={refreshClient}
      />
    </div>
  );
}

// =========================================================================
// Card 1: Content Gap Analysis
// =========================================================================
function ContentGapCard({
  client,
  navigate,
  refreshClient,
}: {
  client: WorkspaceContext["client"];
  navigate: ReturnType<typeof useNavigate>;
  refreshClient: () => Promise<void>;
}) {
  const [data, setData] = useState<ContentGapResult | null>(null);
  const [overrides, setOverrides] = useState<Record<string, ContentGapOverride>>(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [showHiddenUnmatched, setShowHiddenUnmatched] = useState(false);
  // chunk3z-fix398: expand the "site pages not on your GBP" list past the first
  // 8 rows, so every row's Hide button is reachable.
  const [showAllUnmatched, setShowAllUnmatched] = useState(false);
  // fix561: same expand + show-hidden controls for the "No Dedicated Page"
  // (found-as-blurb) section.
  const [showAllNoDedicated, setShowAllNoDedicated] = useState(false);
  const [showHiddenNoDedicated, setShowHiddenNoDedicated] = useState(false);
  // fix567: per-row selection + its own import flag for the "Found on your
  // site" section, so it can send pages to the writing queue independently of
  // the GBP "Missing pages" list (two separate sections, two separate buttons).
  const [selectedFound, setSelectedFound] = useState<Set<string>>(new Set());
  const [importingFound, setImportingFound] = useState(false);
  // chunk3z-fix93: per-row selection for missing pages → writing queue.
  const [selectedMissing, setSelectedMissing] = useState<Set<string>>(new Set());
  const [coveredOpen, setCoveredOpen] = useState(false);
  const toast = useToast();

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [gap, ov] = await Promise.all([
        getContentGap(client.id),
        getContentGapOverrides(client.id).catch(() => ({ overrides: {} })),
      ]);
      setData(gap);
      setOverrides(ov.overrides ?? {});
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load gap analysis.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh().catch(console.error);
  }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Apply manual overrides to the auto-matched data:
   *  - override.url present → row appears in matches with the override URL
   *  - override is null → row appears in missing (user explicitly cleared)
   *  - no override → use auto-match result
   */
  const effective = useMemo(() => {
    if (!data) return null;
    const effMatches: Array<
      ContentGapMatch & { isOverride?: boolean }
    > = [];
    const effMissing: ContentGapMissing[] = [];

    // Helper: is this service marked hidden by the user?
    const isHidden = (serviceName: string): boolean => {
      const ov = overrides[serviceName];
      return !!(ov && typeof ov === "object" && "hidden" in ov && ov.hidden === true);
    };

    // Walk auto-match results, applying overrides. Each match already
    // carries its `kind` (category vs service) — we propagate it through
    // to whichever bucket the override pushes it into.
    for (const m of data.matches) {
      // Hidden services don't appear in counts or rendered groups.
      if (isHidden(m.service)) continue;
      const ov = overrides[m.service];
      if (ov === null) {
        // User cleared this row → push to missing (preserving kind).
        effMissing.push({ name: m.service, kind: m.kind });
      } else if (ov && typeof ov === "object" && "url" in ov && ov.url) {
        // User set a custom URL.
        effMatches.push({
          service: m.service,
          kind: m.kind,
          page: { ...m.page, path: ov.url, url: ov.url },
          isOverride: true,
        });
      } else {
        // No override → use auto-match.
        effMatches.push(m);
      }
    }

    // Walk auto-missing, applying overrides. `s` here is a ContentGapMissing
    // object (`{name, kind}`); we propagate its kind into matches if the
    // user provided a URL.
    for (const s of data.missing) {
      if (isHidden(s.name)) continue;
      const ov = overrides[s.name];
      if (ov && typeof ov === "object" && "url" in ov && ov.url) {
        // User filled in a URL for what was missing.
        effMatches.push({
          service: s.name,
          kind: s.kind,
          page: {
            id: -1,
            path: ov.url,
            url: ov.url,
            title: null,
            wordCount: null,
          },
          isOverride: true,
        });
      } else {
        // No override OR explicitly cleared (null) → still missing.
        effMissing.push(s);
      }
    }

    // fix237: order the missing list as categories first, then services, each
    // group alphabetical by name (case-insensitive). Stable, locale-aware sort.
    effMissing.sort((a, b) => {
      const aCat = a.kind === "category" ? 0 : 1;
      const bCat = b.kind === "category" ? 0 : 1;
      if (aCat !== bCat) return aCat - bCat;
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });

    return { matches: effMatches, missing: effMissing };
  }, [data, overrides]);

  // fix568: the "Found on your site" section (content-first) and this GBP
  // "Missing pages" list can surface the SAME service — a GBP service with no
  // page that also appears as a heading on a hub page. That reads as a
  // duplicate. Rule: a service appears ONCE. If we located it on the site it
  // lives up top in "Found on" (badged GBP); the Missing pages list below shows
  // only GBP services we could NOT find anywhere on the site (genuinely
  // net-new pages). So we filter the found names out of the visible Missing
  // list, its count, select-all, and import. (The headline coverage card still
  // counts every no-dedicated-page service — the found ones are cross-linked
  // there via the GBP badge.)
  const missingNotFound = useMemo(() => {
    if (!effective) return [] as ContentGapMissing[];
    const foundNames = new Set(
      (data?.foundServices ?? [])
        .filter((f) => !isNoDedicatedHidden(f.service)) // hidden found rows don't suppress
        .map((f) => f.service.trim().toLowerCase())
    );
    return effective.missing.filter(
      (m) => !foundNames.has(m.name.trim().toLowerCase())
    );
  }, [effective, data, overrides]); // overrides: isNoDedicatedHidden reads it

  // chunk3z-fix93: keep the missing-selection set in sync with the list.
  // New missing items default to selected (matches the reference's all-checked
  // default so "Add to writing queue" works with no extra clicks); selections
  // for items that left the list are pruned. We track which names we've already
  // seen so a user's explicit uncheck isn't re-checked on the next refresh.
  const seenMissing = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!effective) return;
    // fix568: sync against the DEDUPED list so found-on services (shown up top)
    // never get auto-selected down here.
    const current = missingNotFound.map((m) => m.name);
    const currentSet = new Set(current);
    setSelectedMissing((prev) => {
      const next = new Set<string>();
      for (const name of current) {
        if (seenMissing.current.has(name)) {
          // Already seen: preserve the user's choice.
          if (prev.has(name)) next.add(name);
        } else {
          // Newly appeared: default to selected.
          next.add(name);
        }
      }
      return next;
    });
    seenMissing.current = currentSet;
  }, [effective, missingNotFound]);

  function toggleMissing(name: string) {
    setSelectedMissing((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function toggleSelectAllMissing() {
    if (!effective) return;
    const allNames = missingNotFound.map((m) => m.name);
    setSelectedMissing((prev) =>
      prev.size === allNames.length ? new Set() : new Set(allNames)
    );
  }

  async function handleImport() {
    if (!effective || missingNotFound.length === 0) return;
    const selectedItems = missingNotFound
      .filter((m) => selectedMissing.has(m.name))
      .map((m) => ({ name: m.name, kind: m.kind }));
    if (selectedItems.length === 0) return;
    setImporting(true);
    setError(null);
    try {
      await importMissingToBuildQueue(client.id, {
        items: selectedItems,
      });
      // fix569: no confirmation modal — behaves like the "Found on your site"
      // Add button: toast + refresh in place (so rows reflect the new queue
      // state) rather than a popup + navigate-away.
      toast.show(
        `Added ${selectedItems.length} page${selectedItems.length === 1 ? "" : "s"} to the writing queue.`,
        { kind: "success" }
      );
      await refresh();
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Failed to add to queue"), {
        kind: "error",
      });
    } finally {
      setImporting(false);
    }
  }

  // fix567: is a service/category name already in the writing queue? Derived
  // from the actual articles table (via the gap endpoint), so it persists
  // across reloads and re-crawls — not session state.
  function isInQueue(name: string): boolean {
    const set = data?.existingQueueServices ?? [];
    return set.includes(name.trim().toLowerCase());
  }

  function toggleFound(name: string) {
    setSelectedFound((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  // Send the selected "Found on your site" rows to the writing queue. They're
  // content-first services (not necessarily in GBP), so they import as
  // service_page (category rows aren't produced by this scan). After a
  // successful add we refresh so the rows flip to the "in queue" state.
  async function handleImportFound() {
    const all = data?.foundServices ?? [];
    const selected = all.filter(
      (m) => selectedFound.has(m.service) && !isInQueue(m.service)
    );
    if (selected.length === 0) return;
    setImportingFound(true);
    setError(null);
    try {
      await importMissingToBuildQueue(client.id, {
        items: selected.map((m) => ({ name: m.service, kind: "service" as const })),
      });
      setSelectedFound(new Set());
      toast.show(
        `Added ${selected.length} page${selected.length === 1 ? "" : "s"} to the writing queue.`,
        { kind: "success" }
      );
      await refresh(); // re-derive existingQueueServices so rows flip to "in queue"
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Failed to add to queue"), {
        kind: "error",
      });
    } finally {
      setImportingFound(false);
    }
  }

  async function setOverride(serviceName: string, url: string | null) {
    try {
      const res = await setContentGapOverride(client.id, serviceName, url);
      setOverrides((prev) => ({ ...prev, [serviceName]: res.override }));
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Failed to save override"), {
        kind: "error",
      });
    }
  }

  async function clearOverride(serviceName: string) {
    try {
      await deleteContentGapOverride(client.id, serviceName);
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[serviceName];
        return next;
      });
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Failed to clear override"), {
        kind: "error",
      });
    }
  }

  async function hideService(serviceName: string) {
    try {
      const res = await hideContentGapService(client.id, serviceName);
      setOverrides((prev) => ({ ...prev, [serviceName]: res.override }));
      toast.show(
        `Hidden "${serviceName}" from Content Gap. Toggle "Show hidden" to bring it back.`,
        { kind: "success" }
      );
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Failed to hide"), {
        kind: "error",
      });
    }
  }

  async function unhideService(serviceName: string) {
    try {
      await deleteContentGapOverride(client.id, serviceName);
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[serviceName];
        return next;
      });
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Failed to unhide"), {
        kind: "error",
      });
    }
  }

  // ===== Hide / unhide for "site pages not on your GBP" =====
  // We reuse the content-gap overrides store, namespacing the key so unmatched
  // pages never collide with real GBP service names.
  function unmatchedKey(path: string): string {
    return `__unmatched__:${path}`;
  }
  function isUnmatchedHidden(path: string): boolean {
    const ov = overrides[unmatchedKey(path)];
    return !!(ov && typeof ov === "object" && "hidden" in ov && ov.hidden === true);
  }
  async function hideUnmatched(path: string) {
    const key = unmatchedKey(path);
    try {
      const res = await hideContentGapService(client.id, key);
      setOverrides((prev) => ({ ...prev, [key]: res.override }));
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Failed to hide"), { kind: "error" });
    }
  }
  async function unhideUnmatched(path: string) {
    const key = unmatchedKey(path);
    try {
      await deleteContentGapOverride(client.id, key);
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Failed to unhide"), { kind: "error" });
    }
  }

  // ===== Hide / unhide for "Found on your site, no dedicated page" (fix561) =====
  // Namespaced separately from real service names AND from the unmatched-page
  // hides. Hiding here removes a NOISY row from that section only — the service
  // still counts as a real gap in the left "no dedicated page" card, because it
  // genuinely has no page. This is purely for stripping bad blurb-detection
  // hits (a heading that coincidentally contained the service tokens).
  function noDedicatedKey(name: string): string {
    return `__noded__:${name.toLowerCase()}`;
  }
  function isNoDedicatedHidden(name: string): boolean {
    const ov = overrides[noDedicatedKey(name)];
    return !!(ov && typeof ov === "object" && "hidden" in ov && ov.hidden === true);
  }
  async function hideNoDedicated(name: string) {
    const key = noDedicatedKey(name);
    try {
      const res = await hideContentGapService(client.id, key);
      setOverrides((prev) => ({ ...prev, [key]: res.override }));
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Failed to hide"), { kind: "error" });
    }
  }
  async function unhideNoDedicated(name: string) {
    const key = noDedicatedKey(name);
    try {
      await deleteContentGapOverride(client.id, key);
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Failed to unhide"), { kind: "error" });
    }
  }

  /**
  /**
   * Apply a drag-end action to gbpServices. Handles three cases:
   *   1. Reorder within same category (fromCategory === toCategory, position moves)
   *   2. Move to different category (fromCategory !== toCategory)
   *   3. No-op (drop on self / no position change)
   *
   * Insert position rules:
   *   - If `targetServiceName` is given, insert RIGHT BEFORE that service
   *     in the destination category.
   *   - If targetServiceName is null, append to the end of the destination.
   *
   * Persists via PATCH /api/clients/:id with the new gbpServices map, then
   * refreshes both the client (so Client Settings reflects it) and the
   * Content Gap (so the new ordering shows here).
   */
  async function handleReorderOrMove(
    serviceName: string,
    fromCategory: string,
    toCategory: string,
    targetServiceName: string | null
  ) {
    const current = client.gbpServices ?? {};
    // Deep-clone arrays so we can mutate without aliasing.
    const next: Record<string, string[]> = {};
    for (const [cat, services] of Object.entries(current)) {
      next[cat] = [...(services ?? [])];
    }

    // Remove the service from its source category (case-insensitive match).
    const sourceList = next[fromCategory];
    if (!sourceList) return;
    const sourceIdx = sourceList.findIndex(
      (s) => s.toLowerCase() === serviceName.toLowerCase()
    );
    if (sourceIdx < 0) return;
    const [removed] = sourceList.splice(sourceIdx, 1);
    const movingValue = removed ?? serviceName;

    // Ensure destination exists.
    if (!next[toCategory]) next[toCategory] = [];
    const destList = next[toCategory];

    // Compute insertion index in destination.
    let insertAt = destList.length;
    if (targetServiceName) {
      const targetIdx = destList.findIndex(
        (s) => s.toLowerCase() === targetServiceName.toLowerCase()
      );
      if (targetIdx >= 0) insertAt = targetIdx;
    }

    // Avoid duplicates: if value is already in the dest list, drop the dupe
    // before inserting (defense in depth — shouldn't normally happen since
    // we just removed it, but stays safe).
    const dupeIdx = destList.findIndex(
      (s) => s.toLowerCase() === movingValue.toLowerCase()
    );
    if (dupeIdx >= 0) {
      destList.splice(dupeIdx, 1);
      if (dupeIdx < insertAt) insertAt -= 1;
    }
    destList.splice(insertAt, 0, movingValue);

    // No-op detection: if next === current (same content + order), skip the save.
    const same = JSON.stringify(next) === JSON.stringify(current);
    if (same) return;

    try {
      await updateClient(client.id, { gbpServices: next });
      await refreshClient();
      await refresh();
      if (fromCategory === toCategory) {
        // Within-category reorder — toast is silent (can be noisy if user
        // is rearranging a long list).
      } else {
        toast.show(`Moved "${movingValue}" to ${toCategory}.`, {
          kind: "success",
        });
      }
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Move failed"), { kind: "error" });
    }
  }

  /**
   * Group the effective matches+missing list by category. Walks
   * client.gbpServices to preserve the canonical order, and includes
   * category-only entries (primary, secondaries with no services in the map).
   */
  const groups = useMemo<ContentGapGroup[]>(() => {
    if (!effective) return [];

    // Index match data by lowercased service name for quick lookup.
    const matchByName = new Map<
      string,
      ContentGapMatch & { isOverride?: boolean }
    >();
    for (const m of effective.matches) {
      matchByName.set(m.service.toLowerCase(), m);
    }
    const missingSet = new Set(effective.missing.map((s) => s.name.toLowerCase()));

    // Helper: is this service marked hidden?
    const isHidden = (name: string): boolean => {
      const ov = overrides[name];
      return !!(
        ov && typeof ov === "object" && "hidden" in ov && ov.hidden === true
      );
    };

    // Helper to determine row state from auto-derived data.
    // Hidden services use a synthetic "missing" state — they're filtered
    // out of effective.matches/missing so they wouldn't otherwise appear
    // anywhere, but we still need a row state if we're rendering them
    // (when "Show hidden" toggle is on).
    function rowFor(serviceName: string): ContentGapRowState {
      const m = matchByName.get(serviceName.toLowerCase());
      if (m) return { state: "matched", matchData: m };
      return { state: "missing" };
    }

    const result: ContentGapGroup[] = [];
    const seenCategoriesLower = new Set<string>();

    // Walk gbpServices in insertion order — that's the canonical category order.
    if (client.gbpServices && typeof client.gbpServices === "object") {
      for (const [category, services] of Object.entries(client.gbpServices)) {
        const catLower = category.toLowerCase();
        const inUniverse =
          matchByName.has(catLower) || missingSet.has(catLower);
        result.push({
          category,
          categoryRow: inUniverse ? rowFor(category) : null,
          services: (services ?? []).map((name) => ({
            name,
            row: rowFor(name),
            hidden: isHidden(name),
          })),
        });
        seenCategoriesLower.add(catLower);
      }
    }

    // Fold in primary + secondary categories that are NOT keys in gbpServices.
    const standalone: string[] = [];
    if (
      client.gbpPrimaryCategory &&
      !seenCategoriesLower.has(client.gbpPrimaryCategory.toLowerCase())
    ) {
      standalone.push(client.gbpPrimaryCategory);
    }
    for (const sec of client.gbpSecondaryCategories ?? []) {
      if (!seenCategoriesLower.has(sec.toLowerCase())) {
        standalone.push(sec);
      }
    }
    for (const cat of standalone) {
      result.push({
        category: cat,
        categoryRow: rowFor(cat),
        services: [],
      });
      seenCategoriesLower.add(cat.toLowerCase());
    }

    return result;
  }, [
    effective,
    overrides,
    client.gbpServices,
    client.gbpPrimaryCategory,
    client.gbpSecondaryCategories,
  ]);

  // Track the currently-dragging service for visual feedback (opacity dim
  // on the source row). dnd-kit handles all the actual drop logic for us.
  const [draggingServiceName, setDraggingServiceName] = useState<string | null>(
    null
  );

  // Sensors: pointer with a small drag distance so a click-without-drag
  // doesn't trigger drag detection. 6px feels right.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  function handleDragStart(e: DragStartEvent) {
    const id = String(e.active.id);
    // Active id format: "<categoryName>::<serviceName>"
    const sep = id.indexOf("::");
    if (sep > 0) setDraggingServiceName(id.slice(sep + 2));
  }
  function handleDragEnd(e: DragEndEvent) {
    setDraggingServiceName(null);
    if (!e.over) return;
    const activeId = String(e.active.id);
    const overId = String(e.over.id);
    if (activeId === overId) return;

    // Active id format: "<fromCategory>::<serviceName>"
    const aSep = activeId.indexOf("::");
    if (aSep < 0) return;
    const fromCategory = activeId.slice(0, aSep);
    const serviceName = activeId.slice(aSep + 2);

    // Over id can be either:
    //   "category::<targetCategory>"     → drop on empty space within a category
    //                                      (or onto the category header) → append
    //   "<targetCategory>::<targetService>" → drop on a specific service →
    //                                      insert before that service
    let toCategory: string;
    let targetServiceName: string | null = null;
    if (overId.startsWith("category::")) {
      toCategory = overId.slice("category::".length);
    } else {
      const oSep = overId.indexOf("::");
      if (oSep < 0) return;
      toCategory = overId.slice(0, oSep);
      targetServiceName = overId.slice(oSep + 2);
      // Don't insert "before yourself" — that's a no-op.
      if (
        toCategory === fromCategory &&
        targetServiceName.toLowerCase() === serviceName.toLowerCase()
      ) {
        return;
      }
    }

    handleReorderOrMove(serviceName, fromCategory, toCategory, targetServiceName);
  }
  function handleDragCancel() {
    setDraggingServiceName(null);
  }

  const totalCount =
    (effective?.matches.length ?? 0) + (effective?.missing.length ?? 0);
  const coveragePercent =
    totalCount === 0
      ? 0
      : Math.round(((effective?.matches.length ?? 0) / totalCount) * 100);

  return (
    <>
      {/* Two-way coverage summary (chunk3z-fix102) — plain-language answer to
          "what's missing on my site" and "what's on my site but not my GBP". */}
      {!loading && data && data.hasCrawl && effective && totalCount > 0 && (
        <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card mb-4">
          <h2 className="text-text font-semibold mb-3">Coverage at a glance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-lg border border-border p-4">
              <div className="flex items-baseline gap-2">
                <span
                  className={`text-2xl font-bold ${
                    effective.missing.length === 0 ? "text-success" : "text-warning"
                  }`}
                >
                  {effective.missing.length}
                </span>
                <span className="text-text font-medium text-sm">
                  GBP {effective.missing.length === 1 ? "service" : "services"} with
                  no dedicated page
                </span>
              </div>
              <p className="text-text-subtle text-xs mt-1.5">
                {effective.missing.length === 0
                  ? "Every GBP service has its own page. ✓"
                  : `${effective.matches.length} of ${totalCount} covered. Each missing service needs its own page — listed below.`}
              </p>
            </div>

            <div className="rounded-lg border border-border p-4">
              {(() => {
                const allUnmatched = data.unmatchedPages ?? [];
                const visible = allUnmatched.filter(
                  (p) => !isUnmatchedHidden(p.path ?? p.url)
                );
                const hidden = allUnmatched.filter((p) =>
                  isUnmatchedHidden(p.path ?? p.url)
                );
                return (
                  <>
                    <div className="flex items-baseline gap-2">
                      <span
                        className={`text-2xl font-bold ${
                          visible.length === 0 ? "text-success" : "text-accent"
                        }`}
                      >
                        {visible.length}
                      </span>
                      <span className="text-text font-medium text-sm">
                        site {visible.length === 1 ? "page" : "pages"} not on your
                        GBP
                      </span>
                    </div>
                    <p className="text-text-subtle text-xs mt-1.5">
                      {visible.length === 0
                        ? "Every service page maps to a GBP service. ✓"
                        : "These look like service pages but aren't in your GBP list — add them to GBP, or assign them on the Crawl tab."}
                    </p>
                    {visible.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {(showAllUnmatched ? visible : visible.slice(0, 8)).map(
                          (p) => (
                          <li
                            key={p.id}
                            className="text-xs flex items-center gap-2 group"
                          >
                            <a
                              href={p.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-accent hover:underline font-mono break-all flex-1 min-w-0"
                            >
                              {p.path ?? p.url}
                            </a>
                            <button
                              type="button"
                              onClick={() => hideUnmatched(p.path ?? p.url)}
                              className="shrink-0 text-text-subtle hover:text-danger text-[11px] px-1 opacity-60 hover:opacity-100"
                              title="Hide this page"
                            >
                              Hide
                            </button>
                          </li>
                          )
                        )}
                        {/* chunk3z-fix398: this was static text, so anything past
                            the first 8 rows was unreachable — including its Hide
                            button. Now it expands and collapses the full list. */}
                        {visible.length > 8 && (
                          <li>
                            <button
                              type="button"
                              onClick={() => setShowAllUnmatched((v) => !v)}
                              className="text-accent hover:underline text-xs"
                            >
                              {showAllUnmatched
                                ? "Show fewer"
                                : `+${visible.length - 8} more`}
                            </button>
                          </li>
                        )}
                      </ul>
                    )}
                    {hidden.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-border-subtle">
                        <button
                          type="button"
                          onClick={() => setShowHiddenUnmatched((v) => !v)}
                          className="text-text-subtle hover:text-text text-xs"
                        >
                          {showHiddenUnmatched ? "▾" : "▸"} Hidden ({hidden.length})
                        </button>
                        {showHiddenUnmatched && (
                          <ul className="mt-1.5 space-y-1">
                            {hidden.map((p) => (
                              <li
                                key={p.id}
                                className="text-xs flex items-center gap-2"
                              >
                                <a
                                  href={p.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-text-subtle hover:underline font-mono break-all flex-1 min-w-0 line-through"
                                >
                                  {p.path ?? p.url}
                                </a>
                                <button
                                  type="button"
                                  onClick={() => unhideUnmatched(p.path ?? p.url)}
                                  className="shrink-0 text-accent hover:underline text-[11px] px-1"
                                  title="Unhide this page"
                                >
                                  Unhide
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* No Dedicated Page (fix561, content-first in fix562) — services FOUND
          as headings on /services/ hub pages that have no page of their own.
          Content-first: driven by the pages' actual H2/H3/H4 headings, not the
          GBP list, so it catches on-page services even when they're absent from
          GBP. `inGbp` marks the ones that are also a GBP gap (higher priority).
          Per-row Hide strips any heading that isn't really a service. */}
      {!loading && data && data.hasCrawl && (() => {
        const all = data.foundServices ?? [];
        const visible = all.filter((m) => !isNoDedicatedHidden(m.service));
        const hidden = all.filter((m) => isNoDedicatedHidden(m.service));
        if (all.length === 0) return null;
        // Selectable = visible rows not already in the queue.
        const selectable = visible.filter((m) => !isInQueue(m.service));
        const allSelected =
          selectable.length > 0 &&
          selectable.every((m) => selectedFound.has(m.service));
        const shown = showAllNoDedicated ? visible : visible.slice(0, 12);
        return (
          <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card mb-4">
            <div className="flex items-baseline gap-2 mb-1">
              <h2 className="text-text font-semibold">
                Found on your site, but no dedicated page
              </h2>
              <span className="text-text-subtle text-sm">
                ({visible.length})
              </span>
            </div>
            <p className="text-text-muted text-xs mb-3">
              These services appear as a section on one of your service pages but
              don't have their own page. For SEO, each should get a dedicated
              page. Rows marked <span className="text-accent font-medium">GBP</span>{" "}
              are also a Google Business Profile gap. Select the ones to write and
              add them to the queue; Hide anything that isn't really a service.
            </p>

            {visible.length === 0 ? (
              <p className="text-text-subtle text-xs">
                All found-on-site services here have been hidden.
              </p>
            ) : (
              <>
                {/* Column header row — aligns with the grid below. */}
                <div className="grid grid-cols-[1.25rem_3rem_minmax(9rem,1.5fr)_2fr_auto] items-center gap-2 px-2.5 pb-1 text-[10px] uppercase tracking-wide text-text-subtle font-medium border-b border-border-subtle">
                  <span>
                    {selectable.length > 0 && (
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() =>
                          setSelectedFound(
                            allSelected
                              ? new Set()
                              : new Set(selectable.map((m) => m.service))
                          )
                        }
                        title={allSelected ? "Deselect all" : "Select all"}
                        className="cursor-pointer"
                      />
                    )}
                  </span>
                  <span></span>
                  <span>Service</span>
                  <span>Found on</span>
                  <span></span>
                </div>

                <ul>
                  {shown.map((m) => {
                    const inQueue = isInQueue(m.service);
                    return (
                      <li
                        key={m.service}
                        className={`grid grid-cols-[1.25rem_3rem_minmax(9rem,1.5fr)_2fr_auto] items-center gap-2 px-2.5 py-1.5 text-xs border-b border-border-subtle last:border-0 ${
                          inQueue ? "opacity-50" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={!inQueue && selectedFound.has(m.service)}
                          disabled={inQueue}
                          onChange={() => toggleFound(m.service)}
                          className={inQueue ? "cursor-not-allowed" : "cursor-pointer"}
                          title={
                            inQueue
                              ? "Already in the writing queue"
                              : "Select to add to the writing queue"
                          }
                        />
                        <span>
                          {m.inGbp && (
                            <span
                              className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-accent-subtle text-accent"
                              title="Also missing from your Google Business Profile"
                            >
                              GBP
                            </span>
                          )}
                        </span>
                        <span className="text-text font-medium truncate" title={m.service}>
                          {m.service}
                        </span>
                        <a
                          href={m.onUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent hover:underline truncate min-w-0"
                          title={m.onUrl}
                        >
                          {m.onTitle?.trim() || m.onPath}
                        </a>
                        <span className="flex items-center gap-2 justify-end">
                          {inQueue ? (
                            <span
                              className="text-success text-[11px] font-medium whitespace-nowrap"
                              title="This page is already in the writing queue"
                            >
                              ✓ In queue
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => hideNoDedicated(m.service)}
                              className="text-text-subtle hover:text-danger text-[11px] px-1 opacity-60 hover:opacity-100"
                              title="Hide — not a real service"
                            >
                              Hide
                            </button>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {visible.length > 12 && (
                  <button
                    type="button"
                    onClick={() => setShowAllNoDedicated((v) => !v)}
                    className="text-accent hover:underline text-xs mt-2"
                  >
                    {showAllNoDedicated
                      ? "Show fewer"
                      : `+${visible.length - 12} more`}
                  </button>
                )}

                {/* Add-to-queue footer for THIS section (separate from the GBP
                    Missing pages button below). */}
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleImportFound}
                    disabled={importingFound || selectedFound.size === 0}
                  >
                    {importingFound
                      ? "Adding…"
                      : `+ Add ${selectedFound.size || ""} to writing queue`.replace("  ", " ")}
                  </Button>
                  <span className="text-text-subtle text-xs">
                    {selectable.length === 0
                      ? "Every found service is already in the queue."
                      : "Select pages above, then add them all at once."}
                  </span>
                </div>
              </>
            )}

            {hidden.length > 0 && (
              <div className="mt-3 pt-2 border-t border-border-subtle">
                <button
                  type="button"
                  onClick={() => setShowHiddenNoDedicated((v) => !v)}
                  className="text-text-subtle hover:text-text text-xs"
                >
                  {showHiddenNoDedicated ? "▾" : "▸"} Hidden ({hidden.length})
                </button>
                {showHiddenNoDedicated && (
                  <ul className="mt-1.5 space-y-1">
                    {hidden.map((m) => (
                      <li
                        key={m.service}
                        className="text-xs flex items-center gap-2"
                      >
                        <span className="text-text-subtle line-through flex-1 min-w-0 truncate">
                          {m.service} — {m.onTitle?.trim() || m.onPath}
                        </span>
                        <button
                          type="button"
                          onClick={() => unhideNoDedicated(m.service)}
                          className="shrink-0 text-accent hover:underline text-[11px] px-1"
                          title="Unhide"
                        >
                          Unhide
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      })()}

    <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-text font-semibold">Service coverage</h2>
          <p className="text-text-muted text-xs mt-0.5">
            Every service on your Google Business Profile, matched to an existing
            page on your site.
          </p>
        </div>
        {!loading && data && data.hasCrawl && effective && totalCount > 0 && (
          <span className="text-xs text-text-muted shrink-0 mt-0.5">
            {effective.matches.length} of {totalCount} covered{" "}
            <span
              className={
                coveragePercent >= 50
                  ? "text-success font-semibold"
                  : "text-warning font-semibold"
              }
            >
              {coveragePercent}%
            </span>
          </span>
        )}
      </div>

      {loading && (
        <p className="text-text-muted text-sm">Loading gap analysis...</p>
      )}

      {!loading && error && (
        <p className="text-danger text-sm">{error}</p>
      )}

      {!loading && data && !data.hasCrawl && (
        <p className="text-text-muted text-sm">
          Run a site crawl first to see your content gap analysis.
        </p>
      )}

      {!loading && data && data.hasCrawl && effective && (
        <>
          <div className="h-2 bg-bg-subtle rounded-full overflow-hidden mb-4">
            <div
              className="h-full bg-accent transition-all"
              style={{ width: `${coveragePercent}%` }}
            />
          </div>

          {totalCount === 0 ? (
            <p className="text-text-muted text-sm">
              No GBP categories or services to compare against. Add them in
              Client Settings.
            </p>
          ) : (
            <>
              {/* Missing pages — checkbox list (reference layout). fix568:
                  uses missingNotFound so services already shown in "Found on
                  your site" above don't repeat here. This list is now strictly
                  GBP services with NO presence on the site at all. */}
              {missingNotFound.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-text font-semibold text-sm">
                      Missing pages{" "}
                      <span className="text-text-muted font-normal">
                        {missingNotFound.length} GBP service
                        {missingNotFound.length === 1 ? "" : "s"} not on your
                        site yet
                      </span>
                    </p>
                    <button
                      type="button"
                      onClick={toggleSelectAllMissing}
                      className="text-accent text-xs hover:underline shrink-0"
                    >
                      {selectedMissing.size === missingNotFound.length
                        ? "Deselect all"
                        : "Select all"}
                    </button>
                  </div>

                  <div className="border border-border rounded-lg overflow-hidden">
                    {missingNotFound.map((m) => (
                      <label
                        key={m.name}
                        className="group flex items-start gap-2.5 px-3 py-2 border-b border-border-subtle last:border-b-0 hover:bg-bg-hover/40 cursor-pointer text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={selectedMissing.has(m.name)}
                          onChange={() => toggleMissing(m.name)}
                          className="accent-accent shrink-0 mt-0.5"
                        />
                        <span className="mt-0.5 shrink-0">
                          <KindPill kind={m.kind} />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="text-text font-medium block truncate">
                            {m.name}
                          </span>
                          {m.blurbOnPath && (
                            <span className="text-text-subtle text-xs block truncate">
                              currently a section on{" "}
                              {m.blurbOnUrl ? (
                                <a
                                  href={m.blurbOnUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-accent hover:underline font-mono"
                                >
                                  {m.blurbOnPath}
                                </a>
                              ) : (
                                <span className="font-mono">{m.blurbOnPath}</span>
                              )}
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            hideService(m.name);
                          }}
                          className="opacity-0 group-hover:opacity-100 transition text-text-subtle hover:text-danger text-xs shrink-0 mt-0.5"
                          title="Hide — not a real service"
                        >
                          Hide
                        </button>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Covered services — collapsible */}
              {effective.matches.length > 0 && (
                <div className="mb-4 border border-border rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setCoveredOpen((v) => !v)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-bg-hover/40"
                  >
                    <span className="text-text-subtle text-xs">
                      {coveredOpen ? "▾" : "▸"}
                    </span>
                    <span className="text-text font-semibold text-sm">
                      Covered services{" "}
                      <span className="text-text-muted font-normal">
                        ({effective.matches.length})
                      </span>
                    </span>
                    <span className="text-text-subtle text-xs ml-1">
                      Services with a matching page already
                    </span>
                  </button>
                  {coveredOpen && (
                    <div className="border-t border-border-subtle">
                      {effective.matches.map((m) => (
                        <div
                          key={m.service}
                          className="flex items-center gap-2.5 px-3 py-2 border-b border-border-subtle last:border-b-0 text-sm"
                        >
                          <span className="text-success shrink-0">
                            <CheckIcon />
                          </span>
                          <span className="flex-1 min-w-0 pr-2 flex items-center gap-1.5">
                            <span className="text-text truncate">
                              {m.service}
                            </span>
                            {m.variantOnPath && (
                              <span
                                className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-text-subtle bg-bg-subtle border border-border rounded px-1.5 py-0.5"
                                title="Covered as a variant nested on a shared parent page"
                              >
                                variant
                              </span>
                            )}
                          </span>
                          <a
                            href={m.page.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent hover:underline font-mono text-xs text-right truncate max-w-[45%]"
                            title={m.page.path ?? m.page.url}
                          >
                            {m.page.path ?? m.page.url}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Hidden toggle */}
              {(() => {
                const hiddenCount = Object.values(overrides).filter(
                  (ov) =>
                    ov &&
                    typeof ov === "object" &&
                    "hidden" in ov &&
                    ov.hidden === true
                ).length;
                if (hiddenCount === 0) return null;
                return (
                  <div className="mb-3 flex items-center gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => setShowHidden((v) => !v)}
                      className="text-accent hover:underline"
                    >
                      {showHidden
                        ? `Hide ${hiddenCount} hidden`
                        : `Show ${hiddenCount} hidden`}
                    </button>
                  </div>
                );
              })()}

              {showHidden && (
                <HiddenServicesList overrides={overrides} onUnhide={unhideService} />
              )}
            </>
          )}

          {/* fix568: only claim full coverage when there are no gaps of EITHER
              kind — no un-found GBP gaps AND nothing sitting in "Found on". */}
          {missingNotFound.length === 0 &&
            (data?.foundServices ?? []).filter(
              (f) => !isNoDedicatedHidden(f.service)
            ).length === 0 &&
            effective.matches.length > 0 && (
              <p className="text-accent text-sm flex items-center gap-2">
                <CheckIcon />
                Every service in your GBP universe has a matching page.
              </p>
            )}

          {missingNotFound.length > 0 && (
            <div className="flex items-center gap-3 pt-1">
              <Button
                variant="primary"
                onClick={handleImport}
                disabled={importing || selectedMissing.size === 0}
              >
                + Add to writing queue
                {selectedMissing.size > 0 ? ` (${selectedMissing.size})` : ""}
              </Button>
              {selectedMissing.size === 0 && (
                <span className="text-text-subtle text-xs">
                  Select at least one page to add.
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
    </>
  );
}


// =========================================================================
// HiddenServicesList — services the user has hidden from the gap analysis
// =========================================================================
function HiddenServicesList({
  overrides,
  onUnhide,
}: {
  overrides: Record<string, ContentGapOverride>;
  onUnhide: (serviceName: string) => void;
}) {
  const hiddenItems = useMemo(() => {
    const out: { name: string }[] = [];
    for (const [name, ov] of Object.entries(overrides)) {
      if (ov && typeof ov === "object" && "hidden" in ov && ov.hidden === true) {
        out.push({ name });
      }
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [overrides]);

  if (hiddenItems.length === 0) return null;

  return (
    <div className="mb-4">
      <p className="text-text-subtle text-xs mb-2 uppercase tracking-wide">
        Hidden
      </p>
      <div className="space-y-1">
        {hiddenItems.map((h) => (
          <div
            key={h.name}
            className="flex items-center gap-2 text-text-subtle text-sm py-1"
          >
            <span className="text-text-subtle line-through">{h.name}</span>
            <button
              type="button"
              onClick={() => onUnhide(h.name)}
              className="text-accent text-xs underline ml-auto"
            >
              Unhide
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// =========================================================================
// FlatGapList — flat (non-grouped) list of matches + missing services
// =========================================================================
//
// Replaces the DnD-grouped-by-category view. Renders two sections:
//   1. Existing pages (matches): "<KindPill> <CheckIcon> <name> -- <path>"
//   2. Missing pages: "<KindPill> <CrossIcon> <name>"
//
// Each row carries a category/service kind pill so the user can tell
// which entries are GBP categories (primary + secondary) and which are
// GBP services. The original DnD/category-reassignment workflow is
// removed in this view; users can still hide/clear/override via the
// hover-action menu on each row.
function FlatGapList({
  matches,
  missing,
  overrides,
  showHidden,
  onSetOverride,
  onClearOverride,
  onHide,
  onUnhide,
}: {
  matches: Array<ContentGapMatch & { isOverride?: boolean }>;
  missing: ContentGapMissing[];
  overrides: Record<string, ContentGapOverride>;
  showHidden: boolean;
  onSetOverride: (serviceName: string, url: string | null) => void;
  onClearOverride: (serviceName: string) => void;
  onHide: (serviceName: string) => void;
  onUnhide: (serviceName: string) => void;
}) {
  // Hidden services: we only render them when showHidden is true. They
  // come from `overrides` (not from matches/missing — those are already
  // filtered upstream in `effective`).
  const hiddenItems = useMemo(() => {
    if (!showHidden) return [];
    const out: { name: string }[] = [];
    for (const [name, ov] of Object.entries(overrides)) {
      if (
        ov &&
        typeof ov === "object" &&
        "hidden" in ov &&
        ov.hidden === true
      ) {
        out.push({ name });
      }
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [overrides, showHidden]);

  // chunk3z-fix33: count how many matched services share each URL. The
  // auto-classifier's fuzzy matching can pair multiple GBP services to the
  // same page (e.g. Dental Cleaning + Preventative Dentistry both → /general-dentistry).
  // Some of those collisions are correct (Oral/IV/Sedation Dentistry → one
  // sedation page) but others are bugs the user needs to see and fix.
  // Passing the count down lets each row display a "shared with N others"
  // badge and surface its Unmatch button always-visible (not hover-only)
  // so duplicates are easy to scan and clear.
  const urlShareCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of matches) {
      const url = m.page.url;
      if (!url) continue;
      counts.set(url, (counts.get(url) ?? 0) + 1);
    }
    return counts;
  }, [matches]);

  // Surface a banner-level summary of how many services are entangled in
  // duplicates, so the user notices the issue even without scanning every row.
  const duplicateSummary = useMemo(() => {
    let dupRowCount = 0;
    let dupUrlCount = 0;
    for (const count of urlShareCount.values()) {
      if (count > 1) {
        dupUrlCount += 1;
        dupRowCount += count;
      }
    }
    return { dupRowCount, dupUrlCount };
  }, [urlShareCount]);

  return (
    <div className="space-y-3 mb-4">
      {/* fix33: Duplicate-URL summary banner. Shown when 2+ services share a URL.
          The fuzzy auto-matcher sometimes mispairs GBP services to the wrong
          existing page; this surfaces it so the user knows to review. */}
      {duplicateSummary.dupUrlCount > 0 && (
        <div className="bg-warning/10 border border-warning/30 rounded-md px-3 py-2 text-xs text-text-muted">
          <span className="text-warning font-medium">⚠ Duplicate URLs detected:</span>{" "}
          {duplicateSummary.dupRowCount} services share {duplicateSummary.dupUrlCount}{" "}
          {duplicateSummary.dupUrlCount === 1 ? "URL" : "URLs"}. Some are legitimate (e.g. Oral
          Sedation + IV Sedation correctly map to one sedation page), but others may be
          fuzzy-match errors. Click <span className="font-mono text-text">Unmatch</span> on
          any wrong pairing to clear it.
        </div>
      )}

      {/* Existing pages section */}
      {matches.length > 0 && (
        <div className="space-y-px">
          {matches.map((m) => (
            <FlatRow
              key={m.service}
              kind={m.kind}
              name={m.service}
              state="matched"
              path={m.page.path}
              url={m.page.url}
              isOverride={m.isOverride}
              sharedWithCount={(urlShareCount.get(m.page.url) ?? 1) - 1}
              onClear={() => onClearOverride(m.service)}
              onHide={() => onHide(m.service)}
              onSetUrl={(url) => onSetOverride(m.service, url)}
            />
          ))}
        </div>
      )}

      {/* Missing pages section */}
      {missing.length > 0 && (
        <div>
          <p className="text-text-muted text-xs mb-1.5">Missing pages for:</p>
          <div className="space-y-px">
            {missing.map((m) => (
              <FlatRow
                key={m.name}
                kind={m.kind}
                name={m.name}
                state="missing"
                sharedWithCount={0}
                onHide={() => onHide(m.name)}
                onSetUrl={(url) => onSetOverride(m.name, url)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Hidden section — only when toggled on */}
      {showHidden && hiddenItems.length > 0 && (
        <div>
          <p className="text-text-subtle text-xs mb-2 uppercase tracking-wide">
            Hidden
          </p>
          <div className="space-y-1">
            {hiddenItems.map((h) => (
              <div
                key={h.name}
                className="flex items-center gap-2 text-text-subtle text-sm py-1"
              >
                <span className="text-text-subtle line-through">{h.name}</span>
                <button
                  type="button"
                  onClick={() => onUnhide(h.name)}
                  className="text-accent text-xs underline ml-auto"
                >
                  Unhide
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =========================================================================
// FlatRow — one row in the flat list
// =========================================================================
function FlatRow({
  kind,
  name,
  state,
  path,
  url,
  isOverride,
  sharedWithCount,
  onClear,
  onHide,
  onSetUrl,
}: {
  kind: "category" | "service";
  name: string;
  state: "matched" | "missing";
  path?: string;
  url?: string;
  isOverride?: boolean;
  /** How many OTHER services also map to this URL (0 = unique) */
  sharedWithCount: number;
  onClear?: () => void;
  onHide: () => void;
  onSetUrl: (url: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draftUrl, setDraftUrl] = useState(url ?? "");
  const isDuplicate = state === "matched" && sharedWithCount > 0;

  function commitEdit() {
    const trimmed = draftUrl.trim();
    if (trimmed) onSetUrl(trimmed);
    setEditing(false);
  }

  return (
    <div className="group flex items-center gap-1.5 text-xs py-0.5 leading-4">
      {/* State icon — first */}
      <span
        className={
          state === "matched" ? "text-accent" : "text-danger"
        }
        aria-label={state === "matched" ? "Has page" : "Missing page"}
      >
        {state === "matched" ? <CheckIcon /> : <CrossIcon />}
      </span>

      {/* Category/Service pill — second */}
      <KindPill kind={kind} />

      {/* Name */}
      <span className="text-text font-medium">{name}</span>

      {/* Path / URL or inline editor */}
      {state === "matched" && !editing && (
        <span className="text-text-muted">
          {" -- "}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline font-mono text-xs"
          >
            {truncateMiddle(path ?? "", 40)}
          </a>
          {isOverride && (
            <span
              className="ml-1.5 text-[10px] text-text-subtle italic"
              title="Custom URL set manually"
            >
              (custom)
            </span>
          )}
          {/* fix33: badge surfaces fuzzy-match collisions inline. Some are
              correct (sedation variants → one page) but the UI should make
              them visible so the user can review at a glance. */}
          {isDuplicate && (
            <span
              className="ml-1.5 inline-block bg-warning/15 text-warning text-[10px] font-medium px-1.5 py-px rounded"
              title={`This URL is also assigned to ${sharedWithCount} other service${sharedWithCount === 1 ? "" : "s"} above or below`}
            >
              shared with {sharedWithCount} other{sharedWithCount === 1 ? "" : "s"}
            </span>
          )}
        </span>
      )}
      {editing && (
        <span className="flex items-center gap-1.5 ml-1">
          <input
            type="text"
            value={draftUrl}
            onChange={(e) => setDraftUrl(e.target.value)}
            placeholder="https://..."
            className="text-xs px-1.5 py-0.5 bg-bg-input border border-border rounded font-mono w-64"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") setEditing(false);
            }}
          />
          <button
            type="button"
            onClick={commitEdit}
            className="text-accent text-xs"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-text-subtle text-xs"
          >
            Cancel
          </button>
        </span>
      )}

      {/* fix33: Always-visible Unmatch button on duplicate rows. The hover-only
          icon is too subtle when scanning specifically for fuzzy-match errors,
          and naming the action explicitly ("Unmatch") makes the consequence clear.

          Important: passes null to onSetUrl (which propagates as a `null`
          override to the backend). This pushes the row into Missing pages.
          The hover-only ✕ button on non-duplicate rows still uses onClear
          (delete override), but that only un-overrides — for an auto-matched
          row with no existing override, onClear is a no-op. Unmatch is the
          right action for breaking auto-match pairings. */}
      {!editing && isDuplicate && (
        <button
          type="button"
          onClick={() => onSetUrl(null)}
          className="ml-2 text-warning hover:text-warning text-xs font-medium underline-offset-2 hover:underline"
          title="Clear this pairing — the service moves to Missing pages"
          aria-label="Unmatch"
        >
          Unmatch
        </button>
      )}

      {/* Action icons — hover only, sit just past the content */}
      {!editing && (
        <span className="ml-2 opacity-0 group-hover:opacity-100 transition flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => {
              setDraftUrl(url ?? "");
              setEditing(true);
            }}
            className="text-text-muted hover:text-text"
            title={state === "matched" ? "Edit URL" : "Add custom URL"}
            aria-label="Edit URL"
          >
            <PencilIcon />
          </button>
          {state === "matched" && onClear && !isDuplicate && (
            <button
              type="button"
              onClick={onClear}
              className="text-text-muted hover:text-warning text-xs"
              title="Mark as missing"
              aria-label="Mark as missing"
            >
              ✕
            </button>
          )}
          <button
            type="button"
            onClick={onHide}
            className="text-text-muted hover:text-danger text-xs"
            title="Hide from Content Gap (not a real service)"
            aria-label="Hide"
          >
            🗑
          </button>
        </span>
      )}
    </div>
  );
}

// =========================================================================
// KindPill — small badge labeling a row as a Category or Service
// =========================================================================
//
// Visual reference: mockup had a "CONCEPT"-style blue pill and a
// "STRATEGY"-style amber pill, side-by-side at the right of each row.
// We use the same uppercase / pill / muted-color treatment here. Width
// is fixed (w-20) so all pills line up vertically across rows.
function KindPill({ kind }: { kind: "category" | "service" }) {
  // Categories: blue (matches the app's accent-subtle palette)
  // Services:   green (matches the app's success palette — used elsewhere
  //             for "✓ Written" and similar positive states). Picked over
  //             the original amber/warning treatment because warning
  //             colors signal "caution/problem" which doesn't fit
  //             "this is a service the business offers." Green pairs
  //             well with blue (different hue family, similar saturation)
  //             and reads as positive/active.
  const styles =
    kind === "category"
      ? "bg-accent-subtle text-accent border-accent/30"
      : "bg-success-bg text-success border-success-border";
  return (
    <span
      className={`${styles} border text-[9px] font-bold uppercase tracking-wider px-1.5 py-0 rounded-full w-16 text-center inline-block flex-shrink-0`}
    >
      {kind === "category" ? "Category" : "Service"}
    </span>
  );
}

// =========================================================================
// Path/URL truncation helper
// =========================================================================
function truncateMiddle(s: string, max: number): string {
  if (s.length <= max) return s;
  const half = Math.floor((max - 3) / 2);
  return `${s.slice(0, half)}...${s.slice(s.length - half)}`;
}

// =========================================================================
// CategoryGroup — one category's section (header row + service rows)
// =========================================================================
//
// Layout: a single CSS grid with three columns shared across the category
// header AND all of its service rows, so action icons line up at the same
// x-position. Column template:
//
//   col 1 (max-content): drag handle + check/X + name + override pill
//   col 2 (max-content): "—" + URL link or inline editor
//   col 3 (1fr):         action icons (pencil / X / revert), hover-only
//
// The first two columns size to their longest content; col 3 fills the
// remaining width but the icons themselves sit at its left edge — so
// effectively the icons appear "just past the longest URL," not at the
// far right edge of the card.
//
// Drop-zone behavior: dnd-kit's useDroppable wraps the whole grid. When
// a service is being dragged from elsewhere, this container highlights.
function CategoryGroup({
  group,
  overrides,
  draggingServiceName,
  showHidden,
  onSetOverride,
  onClearOverride,
  onHide,
  onUnhide,
}: {
  group: ContentGapGroup;
  overrides: Record<string, ContentGapOverride>;
  draggingServiceName: string | null;
  showHidden: boolean;
  onSetOverride: (name: string, url: string | null) => void;
  onClearOverride: (name: string) => void;
  onHide: (name: string) => void;
  onUnhide: (name: string) => void;
}) {
  // Drop target — id format "category::<name>" so DragEnd can route it.
  const { setNodeRef, isOver, active } = useDroppable({
    id: `category::${group.category}`,
  });

  // Highlight only when the active drag is FROM a different category.
  // (Re-using the activeId parsing inline since it's tiny.)
  const activeFromCategory = (() => {
    if (!active?.id) return null;
    const id = String(active.id);
    const sep = id.indexOf("::");
    return sep > 0 ? id.slice(0, sep) : null;
  })();
  const isValidDropTarget =
    activeFromCategory !== null && activeFromCategory !== group.category;
  const showHighlight = isOver && isValidDropTarget;

  // Row-wide hover tracking. Each cell of a row sets this to the row's key
  // on mouseEnter; we clear it only when the mouse leaves the entire grid
  // (handled by the grid container's onMouseLeave). This avoids flicker
  // when moving the mouse between sibling cells of the same row.
  const [hoveredRowKey, setHoveredRowKey] = useState<string | null>(null);

  return (
    <div
      ref={setNodeRef}
      className={`rounded-md transition-colors ${
        showHighlight ? "bg-accent/5 ring-1 ring-accent/30" : ""
      }`}
    >
      <div
        className="grid grid-cols-[max-content_max-content_1fr] gap-x-3 gap-y-1.5 items-baseline"
        onMouseLeave={() => setHoveredRowKey(null)}
      >
        {/* Category header row (top of group) */}
        {group.categoryRow ? (
          <CategoryHeaderRow
            rowKey={`__cat__${group.category}`}
            hoveredRowKey={hoveredRowKey}
            setHoveredRowKey={setHoveredRowKey}
            category={group.category}
            row={group.categoryRow}
            isOverride={
              group.categoryRow.state === "matched"
                ? !!group.categoryRow.matchData.isOverride
                : overrides[group.category] === null
            }
            onSetOverride={(url) => onSetOverride(group.category, url)}
            onUseAuto={() => onClearOverride(group.category)}
            onMarkMissing={() => onSetOverride(group.category, null)}
          />
        ) : (
          // Category isn't in the gap universe (has services but no
          // category-level scoring). Render the header anyway, spanning
          // all 3 columns so it's visually distinct.
          <div className="col-span-3 text-text font-medium text-sm flex items-baseline gap-2">
            <span className="text-text-subtle">📂</span>
            <span>{group.category}</span>
          </div>
        )}

        {/* Service rows — wrapped in a SortableContext so they can reorder
            within this category. Each item id matches its rowKey so dnd-kit
            can map drop targets to specific positions.
            Hidden services are filtered out unless `showHidden` is on. */}
        <SortableContext
          items={group.services
            .filter((s) => !s.hidden || showHidden)
            .map((s) => `${group.category}::${s.name}`)}
          strategy={verticalListSortingStrategy}
        >
          {group.services
            .filter((s) => !s.hidden || showHidden)
            .map((s) => (
              <ServiceRow
                key={`${group.category}-${s.name}`}
                rowKey={`${group.category}::${s.name}`}
                hoveredRowKey={hoveredRowKey}
                setHoveredRowKey={setHoveredRowKey}
                categoryName={group.category}
                serviceName={s.name}
                row={s.row}
                isOverride={
                  s.row.state === "matched"
                    ? !!s.row.matchData.isOverride
                    : overrides[s.name] === null
                }
                isHidden={s.hidden}
                isDragging={
                  draggingServiceName?.toLowerCase() === s.name.toLowerCase()
                }
                onSetOverride={(url) => onSetOverride(s.name, url)}
                onUseAuto={() => onClearOverride(s.name)}
                onMarkMissing={() => onSetOverride(s.name, null)}
                onHide={() => onHide(s.name)}
                onUnhide={() => onUnhide(s.name)}
              />
            ))}
        </SortableContext>
      </div>
    </div>
  );
}

// =========================================================================
// CategoryHeaderRow — non-draggable row representing a GBP category
// =========================================================================
function CategoryHeaderRow({
  rowKey,
  hoveredRowKey,
  setHoveredRowKey,
  category,
  row,
  isOverride,
  onSetOverride,
  onUseAuto,
  onMarkMissing,
}: {
  rowKey: string;
  hoveredRowKey: string | null;
  setHoveredRowKey: (k: string | null) => void;
  category: string;
  row: ContentGapRowState;
  isOverride: boolean;
  onSetOverride: (url: string) => void;
  onUseAuto: () => void;
  onMarkMissing: () => void;
}) {
  const isHovered = hoveredRowKey === rowKey;
  const enter = () => setHoveredRowKey(rowKey);

  const url = row.state === "matched" ? row.matchData.page.path : null;
  const fullUrl = row.state === "matched" ? row.matchData.page.url : null;

  return (
    <RowCells
      rowKey={rowKey}
      isHovered={isHovered}
      enter={enter}
      isCategory={true}
      state={row.state}
      url={url}
      fullUrl={fullUrl}
      name={category}
      isOverride={isOverride}
      onSetOverride={onSetOverride}
      onUseAuto={onUseAuto}
      onMarkMissing={onMarkMissing}
    />
  );
}

// =========================================================================
// ServiceRow — draggable row representing a GBP service
// =========================================================================
function ServiceRow({
  rowKey,
  hoveredRowKey,
  setHoveredRowKey,
  categoryName,
  serviceName,
  row,
  isOverride,
  isHidden,
  isDragging,
  onSetOverride,
  onUseAuto,
  onMarkMissing,
  onHide,
  onUnhide,
}: {
  rowKey: string;
  hoveredRowKey: string | null;
  setHoveredRowKey: (k: string | null) => void;
  categoryName: string;
  serviceName: string;
  row: ContentGapRowState;
  isOverride: boolean;
  isHidden: boolean;
  isDragging: boolean;
  onSetOverride: (url: string) => void;
  onUseAuto: () => void;
  onMarkMissing: () => void;
  onHide: () => void;
  onUnhide: () => void;
}) {
  // dnd-kit sortable. id format "<categoryName>::<serviceName>" so the
  // DndContext-level onDragEnd can route both reorder and cross-category move.
  // useSortable also handles within-category reorder via positional drop targets.
  const dragId = `${categoryName}::${serviceName}`;
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: dragId });

  const isHovered = hoveredRowKey === rowKey;
  const enter = () => setHoveredRowKey(rowKey);

  const url = row.state === "matched" ? row.matchData.page.path : null;
  const fullUrl = row.state === "matched" ? row.matchData.page.url : null;

  // While dragging, dim the source row. When hidden + showing, dim the row
  // so the user remembers it's not actively counted.
  const opacityClass = isDragging
    ? "opacity-40"
    : isHidden
    ? "opacity-50"
    : "";

  // Pass dnd-kit's setNodeRef + listeners + transform down to the cell that
  // owns the drag handle. Other cells stay non-draggable so clicking the URL
  // or the action buttons doesn't accidentally start a drag.
  return (
    <RowCells
      rowKey={rowKey}
      isHovered={isHovered}
      enter={enter}
      isCategory={false}
      state={row.state}
      url={url}
      fullUrl={fullUrl}
      name={serviceName}
      isOverride={isOverride}
      isHidden={isHidden}
      opacityClass={opacityClass}
      drag={{ setNodeRef, listeners, attributes, transform, transition }}
      onSetOverride={onSetOverride}
      onUseAuto={onUseAuto}
      onMarkMissing={onMarkMissing}
      onHide={onHide}
      onUnhide={onUnhide}
    />
  );
}

// =========================================================================
// RowCells — three sibling grid cells (name, URL, actions)
// =========================================================================
//
// Each cell handles its own mouseEnter (all reporting the same rowKey, so
// moving between cells doesn't flicker). The grid container's mouseLeave
// (in CategoryGroup) clears hover when the mouse leaves the whole grid.
//
// For draggable rows: dnd-kit's setNodeRef + listeners go on cell 1 only.
// The drag transform style is also applied to cell 1, since the other
// cells stay in place (they're sibling grid items). dnd-kit's overlay
// shows the drag visual; we just dim the source.
function RowCells({
  rowKey,
  isHovered,
  enter,
  isCategory,
  state,
  url,
  fullUrl,
  name,
  isOverride,
  isHidden = false,
  opacityClass = "",
  drag,
  onSetOverride,
  onUseAuto,
  onMarkMissing,
  onHide,
  onUnhide,
}: {
  rowKey: string;
  isHovered: boolean;
  enter: () => void;
  isCategory: boolean;
  state: "matched" | "missing";
  url: string | null;
  fullUrl: string | null;
  name: string;
  isOverride: boolean;
  isHidden?: boolean;
  opacityClass?: string;
  drag?: {
    setNodeRef: (n: HTMLElement | null) => void;
    listeners: DraggableSyntheticListeners;
    attributes: DraggableAttributes;
    /** dnd-kit/sortable's actual Transform type (CSS.Transform.toString-compatible). */
    transform: Transform | null;
    transition: string | undefined;
  };
  onSetOverride: (url: string) => void;
  onUseAuto: () => void;
  onMarkMissing: () => void;
  /** Hide button only renders for service rows (not categories) when defined. */
  onHide?: () => void;
  /** Unhide button renders only when isHidden=true. */
  onUnhide?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(url ?? "");

  function startEdit() {
    setDraft(url ?? "");
    setEditing(true);
  }
  function saveEdit() {
    const v = draft.trim();
    if (v.length === 0) onMarkMissing();
    else onSetOverride(v);
    setEditing(false);
  }
  function cancelEdit() {
    setEditing(false);
    setDraft(url ?? "");
  }

  // Indent service rows under the category header.
  const indentClass = isCategory ? "" : "ml-5";

  // Name styling: categories always bold; services lighter, slightly bolder
  // (medium weight) when their row is hovered.
  const nameClass = isCategory
    ? "text-text font-medium"
    : `text-[13px] transition-colors transition-[font-weight] duration-100 ${
        isHovered
          ? "text-text font-medium"
          : "text-text-muted font-normal"
      }`;

  // Drag transform applied to cell 1 so the grabbed cell tracks the cursor.
  // Drag transform applied to cell 1 so the grabbed cell tracks the cursor.
  // CSS.Transform.toString handles the dnd-kit/sortable transform shape
  // including the scale/rotate hints. Pair with `transition` for the smooth
  // animation when other items reorder around the dragged one.
  const transformStyle = drag
    ? {
        transform: CSS.Transform.toString(drag.transform),
        transition: drag.transition,
      }
    : undefined;

  // Display-truncate URLs longer than 50 chars (full URL stays in href + title).
  const displayUrl =
    url && url.length > 50 ? url.slice(0, 47) + "..." : url;

  return (
    <>
      {/* CELL 1 — name area (and drag-handle origin if draggable) */}
      <div
        ref={drag?.setNodeRef}
        style={transformStyle}
        onMouseEnter={enter}
        className={`text-sm flex items-baseline gap-2 ${indentClass} ${opacityClass}`}
      >
        {drag && (
          <span
            {...drag.attributes}
            {...drag.listeners}
            className={`text-text-subtle text-xs select-none cursor-grab active:cursor-grabbing -ml-3 mr-0.5 transition-opacity ${
              isHovered ? "opacity-100" : "opacity-0"
            }`}
            title="Drag to move to another category"
            aria-label="Drag handle"
          >
            ⋮⋮
          </span>
        )}
        {state === "matched" ? <CheckIcon /> : <CrossIcon />}
        <span className={nameClass}>{name}</span>
      </div>

      {/* CELL 2 — URL or inline editor */}
      <div
        onMouseEnter={enter}
        className={`text-sm flex items-baseline gap-2 min-w-0 ${opacityClass}`}
      >
        {state === "matched" && !editing && (
          <>
            <span className="text-text-subtle">—</span>
            <a
              href={fullUrl ?? "#"}
              target="_blank"
              rel="noreferrer"
              draggable={false}
              className="text-text-muted hover:text-accent font-mono text-xs"
              title={fullUrl ?? ""}
            >
              {displayUrl}
            </a>
            {isOverride && (
              <span
                className="text-[10px] text-purple-600 bg-purple-100 border border-purple-200 px-1.5 py-0.5 rounded shrink-0"
                title="Manually set"
              >
                manual
              </span>
            )}
          </>
        )}
        {editing && (
          <>
            <span className="text-text-subtle">—</span>
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEdit();
                else if (e.key === "Escape") cancelEdit();
              }}
              className="font-mono text-xs px-2 py-0.5 border border-border rounded flex-1 min-w-[260px] max-w-md"
              placeholder="/services/your-service-page/"
              autoFocus
            />
            <button
              type="button"
              onClick={saveEdit}
              className="text-xs text-accent hover:underline shrink-0"
              title="Save"
            >
              Save
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              className="text-xs text-text-subtle hover:text-text-muted shrink-0"
              title="Cancel"
            >
              Cancel
            </button>
          </>
        )}
      </div>

      {/* CELL 3 — action icons. Hover-only. Sit at the LEFT edge of the
          1fr column, so they appear immediately past the longest URL,
          not pushed to the far right edge of the card. */}
      <div
        onMouseEnter={enter}
        className={`text-sm flex items-center gap-1.5 self-center ${opacityClass}`}
      >
        {!editing && (
          <span
            className={`inline-flex items-center gap-1.5 transition-opacity ${
              isHovered ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <button
              type="button"
              onClick={startEdit}
              className="text-text-subtle hover:text-accent leading-none px-1"
              title={state === "matched" ? "Edit URL" : "Set URL"}
              aria-label={state === "matched" ? "Edit URL" : "Set URL"}
            >
              <PencilIcon />
            </button>
            {state === "matched" && (
              <button
                type="button"
                onClick={() => onMarkMissing()}
                className="text-text-subtle hover:text-danger text-base leading-none px-1"
                title="Clear (mark as missing)"
                aria-label="Clear (mark as missing)"
              >
                ✕
              </button>
            )}
            {isOverride && (
              <button
                type="button"
                onClick={onUseAuto}
                className="text-text-subtle hover:text-accent text-base leading-none px-1"
                title="Revert to auto-match"
                aria-label="Revert to auto-match"
              >
                ↻
              </button>
            )}
            {/* Hide / Unhide — service rows only (onHide is undefined on
                category rows). When the row IS hidden, show "↩" to restore.
                When NOT hidden, show "🚫" to hide it. Hidden services are
                excluded from coverage counts. */}
            {onHide && !isHidden && (
              <button
                type="button"
                onClick={onHide}
                className="text-text-subtle hover:text-warning text-sm leading-none px-1"
                title="Hide from Content Gap (not a real service)"
                aria-label="Hide from Content Gap"
              >
                🚫
              </button>
            )}
            {onUnhide && isHidden && (
              <button
                type="button"
                onClick={onUnhide}
                className="text-text-subtle hover:text-accent text-base leading-none px-1"
                title="Restore to Content Gap"
                aria-label="Restore to Content Gap"
              >
                ↩
              </button>
            )}
          </span>
        )}
      </div>
    </>
  );
}

// Diagonal pencil icon — tip pointing down-and-to-the-left. Inline SVG so
// it renders identically across OSes (Unicode pencils vary in orientation).
function PencilIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Pencil body angled top-right (eraser end) → bottom-left (writing tip) */}
      <path d="M19 5l-2-2-12 12-1 4 4-1 12-12-1-1z" />
      {/* Eraser/wood seam */}
      <path d="M14.5 7.5l2 2" />
    </svg>
  );
}


// =========================================================================
// Card 3: Service Entity Overlap Research
// =========================================================================
function EntityOverlapCard({ client }: { client: WorkspaceContext["client"] }) {
  // Default seed keyword = "{primary category} {city}"
  const initialCity = parseCityFromAddress(client.businessAddress);
  const initialSeed = client.gbpPrimaryCategory
    ? `${client.gbpPrimaryCategory.toLowerCase()} ${initialCity.split(",")[0]?.toLowerCase().trim() ?? ""}`.trim()
    : "";

  const allServices = useMemo(() => {
    const result: string[] = [];
    if (client.gbpServices && typeof client.gbpServices === "object") {
      for (const list of Object.values(client.gbpServices)) {
        if (Array.isArray(list)) {
          for (const s of list) {
            if (typeof s === "string" && s.trim()) result.push(s.trim());
          }
        }
      }
    }
    return result;
  }, [client.gbpServices]);

  const [seedKeyword, setSeedKeyword] = useState(initialSeed);
  const [city, setCity] = useState(initialCity);
  const [candidateText, setCandidateText] = useState(allServices.join(", "));
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<EntityOverlapResult | null>(null);
  const [ranAt, setRanAt] = useState<string | null>(null);

  // Load cached result on mount
  useEffect(() => {
    let cancelled = false;
    getEntityOverlap(client.id)
      .then((data) => {
        if (cancelled) return;
        if (data.result) {
          setResult(data.result);
          setRanAt(data.ranAt ?? data.result.ranAt);
          if (data.seedKeyword) setSeedKeyword(data.seedKeyword);
          if (data.city) setCity(data.city);
        }
      })
      .catch((err) => console.error("Load cached overlap failed:", err));
    return () => {
      cancelled = true;
    };
  }, [client.id]);

  // Resync inputs when client changes
  useEffect(() => {
    setSeedKeyword(initialSeed);
    setCity(initialCity);
    setCandidateText(allServices.join(", "));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  async function handleRun() {
    if (!seedKeyword.trim() || !city.trim()) {
      setError("Seed keyword and city are required.");
      return;
    }
    const candidates = candidateText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (candidates.length === 0) {
      setError("Add at least one candidate entity.");
      return;
    }

    setError(null);
    setRunning(true);
    try {
      const data = await runEntityOverlap(client.id, {
        seedKeyword: seedKeyword.trim(),
        city: city.trim(),
        candidateEntities: candidates,
      });
      setResult(data.result);
      setRanAt(data.result.ranAt);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Analysis failed.");
    } finally {
      setRunning(false);
    }
  }

  function handleAutoFill() {
    setCandidateText(allServices.join(", "));
  }

  const distinctEntities = useMemo(() => {
    if (!result) return [];
    return result.rows
      .filter((r) => r.verdict === "very_distinct" || r.verdict === "distinct")
      .map((r) => r.entity);
  }, [result]);

  return (
    <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card">
      <h2 className="text-text font-semibold mb-1">Service Entity Overlap Research</h2>
      <p className="text-text-muted text-sm mb-4">
        Determine which services Google treats as distinct entities worthy of their own page.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Field label="Seed Keyword">
          <TextInput
            value={seedKeyword}
            onChange={setSeedKeyword}
            placeholder="e.g. plumber Chicago"
          />
        </Field>
        <Field label="City">
          <TextInput
            value={city}
            onChange={setCity}
            placeholder="e.g. Chicago, IL"
          />
        </Field>
      </div>

      <Field
        label="Candidate Entities (optional, comma-separated)"
        hint={
          allServices.length > 0
            ? "Auto-filled from your GBP Profile Data services."
            : "Add services to compare. One per comma."
        }
      >
        <TextInput
          value={candidateText}
          onChange={setCandidateText}
          placeholder="Leave blank to auto-detect from category audit, or enter: water heater repair, drain cleaning..."
        />
      </Field>

      <div className="flex items-center gap-3 mt-3">
        <Button
          variant="primary"
          onClick={handleRun}
          disabled={running || !seedKeyword.trim() || !city.trim() || !candidateText.trim()}
        >
          {running ? "↻ Analyzing Entities..." : "🔍 Run Overlap Analysis"}
        </Button>
        {allServices.length > 0 && candidateText !== allServices.join(", ") && (
          <Button variant="secondary" size="sm" onClick={handleAutoFill}>
            Reset to GBP services
          </Button>
        )}
        {error && <span className="text-danger text-sm">{error}</span>}
        {ranAt && !running && !error && (
          <span className="text-text-subtle text-xs">
            Last run {formatTimestamp(ranAt)}
          </span>
        )}
      </div>

      {result?.stubbed && (
        <p className="text-warning text-xs mt-3">
          ⚠ DataForSEO not configured — using stub data. Set DATAFORSEO_LOGIN
          and DATAFORSEO_PASSWORD in Railway to enable real entity overlap.
        </p>
      )}

      {result && result.rows.length > 0 && (
        <>
          <div className="mt-6">
            <h3 className="text-accent font-semibold text-sm mb-2">
              Entity Overlap Results
              <span className="text-text-subtle text-xs font-normal ml-2">
                vs. "{result.seedKeyword}" in {result.city}
              </span>
            </h3>
            <OverlapTable rows={result.rows} />
          </div>

          {distinctEntities.length > 0 && (
            <DistinctEntitiesSummary entities={distinctEntities} />
          )}
        </>
      )}
    </div>
  );
}

function OverlapTable({ rows }: { rows: EntityOverlapRow[] }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggle(entity: string) {
    const next = new Set(expanded);
    if (next.has(entity)) next.delete(entity);
    else next.add(entity);
    setExpanded(next);
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-text-muted text-xs uppercase tracking-wide">
            <th className="text-left py-2 pr-4 font-medium">Service Entity</th>
            <th className="text-left py-2 pr-4 font-medium w-16">Results</th>
            <th className="text-left py-2 pr-4 font-medium w-48">Overlap %</th>
            <th className="text-left py-2 pr-4 font-medium w-32">Verdict</th>
            <th className="text-left py-2 font-medium w-24">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <OverlapRow
              key={row.entity}
              row={row}
              expanded={expanded.has(row.entity)}
              onToggle={() => toggle(row.entity)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OverlapRow({
  row,
  expanded,
  onToggle,
}: {
  row: EntityOverlapRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className="border-t border-border-subtle">
        <td className="py-2 pr-4 text-text font-medium">{row.entity}</td>
        <td className="py-2 pr-4 text-text-muted">{row.resultsCount} biz</td>
        <td className="py-2 pr-4">
          <div className="flex items-center gap-2">
            <div className="h-2 bg-bg-subtle rounded-full overflow-hidden flex-1 max-w-[160px]">
              <div
                className={`h-full ${barColor(row.verdict)}`}
                style={{ width: `${Math.max(row.overlapPercent, 2)}%` }}
              />
            </div>
            <span className="text-text-muted text-xs whitespace-nowrap">
              {row.overlapPercent}%
            </span>
          </div>
        </td>
        <td className="py-2 pr-4">
          <VerdictBadge verdict={row.verdict} />
        </td>
        <td className="py-2">
          <button
            type="button"
            onClick={onToggle}
            className="text-text-muted hover:text-accent text-xs"
          >
            {expanded ? "▾ Hide" : "▸ Details"}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-bg-subtle/40 border-t border-border-subtle">
          <td colSpan={5} className="py-3 px-4">
            <OverlapDetail row={row} />
          </td>
        </tr>
      )}
    </>
  );
}

function OverlapDetail({ row }: { row: EntityOverlapRow }) {
  return (
    <div className="text-xs">
      {row.topBusinesses.length > 0 ? (
        <>
          <p className="text-text-muted font-medium mb-1.5">
            Top {row.topBusinesses.length} businesses for "{row.entity}":
          </p>
          <ol className="space-y-0.5 mb-3">
            {row.topBusinesses.map((b, i) => (
              <li key={i} className="text-text-muted">
                <span className="text-text-subtle">{i + 1}.</span>{" "}
                <span className="text-text font-medium">{b.name}</span>
                {b.rating != null && (
                  <span className="text-warning ml-2">
                    ★ {b.rating}
                  </span>
                )}
                {b.reviewCount != null && (
                  <span className="text-text-subtle ml-1">({b.reviewCount})</span>
                )}
                {b.url && (
                  <span className="ml-2">
                    <a
                      href={b.url.startsWith("http") ? b.url : `https://${b.url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent hover:underline"
                    >
                      {b.url}
                    </a>
                  </span>
                )}
              </li>
            ))}
          </ol>
        </>
      ) : (
        <p className="text-text-subtle italic mb-3">
          No competitor detail available for this entity in stub mode.
        </p>
      )}
      {row.overlappingNames.length > 0 && (
        <p className="text-warning">
          <span className="font-medium">Overlapping with seed:</span>{" "}
          {row.overlappingNames.join(", ")}
        </p>
      )}
      {row.overlappingNames.length === 0 && row.topBusinesses.length > 0 && (
        <p className="text-success text-xs">
          No overlap with the seed SERP — this entity stands on its own.
        </p>
      )}
    </div>
  );
}

function VerdictBadge({ verdict }: { verdict: OverlapVerdict }) {
  const styles: Record<OverlapVerdict, { label: string; classes: string }> = {
    very_distinct: {
      label: "VERY DISTINCT",
      classes: "bg-success-bg text-success border border-success-border",
    },
    distinct: {
      label: "DISTINCT",
      classes: "bg-accent-subtle text-accent border border-accent/30",
    },
    moderate: {
      label: "MODERATE",
      classes: "bg-warning-bg text-warning border border-warning-border",
    },
    high_overlap: {
      label: "HIGH OVERLAP",
      classes: "bg-danger-bg text-danger border border-danger-border",
    },
  };
  const { label, classes } = styles[verdict];
  return (
    <span
      className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${classes}`}
    >
      {label}
    </span>
  );
}

function barColor(verdict: OverlapVerdict): string {
  switch (verdict) {
    case "very_distinct":
      return "bg-success";
    case "distinct":
      return "bg-accent";
    case "moderate":
      return "bg-warning";
    case "high_overlap":
      return "bg-danger";
  }
}

function DistinctEntitiesSummary({ entities }: { entities: string[] }) {
  return (
    <div className="mt-6 bg-success-bg/30 border border-success-border rounded-lg p-4">
      <h3 className="text-success font-semibold text-sm mb-2">
        {entities.length} Distinct Entities Need Their Own Page
      </h3>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {entities.map((e) => (
          <span
            key={e}
            className="bg-success-bg text-success border border-success-border text-xs px-2 py-1 rounded font-medium"
          >
            {e}
          </span>
        ))}
      </div>
      <p className="text-text-subtle text-xs">
        These will feed into your Content Plan as items needing dedicated pages.
      </p>
    </div>
  );
}

// =========================================================================
// Helpers
// =========================================================================
// =========================================================================
// Icons (inline SVG so they scale cleanly with text)
// =========================================================================
function CheckIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-accent flex-shrink-0 inline-block"
      style={{ verticalAlign: "-2px" }}
      aria-label="Has matching page"
    >
      <path d="M3 8.5l3.5 3.5L13 5" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-danger flex-shrink-0 inline-block"
      style={{ verticalAlign: "-2px" }}
      aria-label="Missing"
    >
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

function parseCityFromAddress(address: string | null): string {
  if (!address) return "";
  // fix282: handle two-line addresses (newline between suite and city) so the
  // suite and city don't get mashed ("...#270Charlotte"). Split on newlines too.
  const parts = address
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length < 2) return "";
  const city = parts[parts.length - 2] ?? "";
  const stateZip = parts[parts.length - 1] ?? "";
  const stateMatch = stateZip.match(/^([A-Z]{2})/);
  if (city && stateMatch) {
    return `${city}, ${stateMatch[1]}`;
  }
  return city;
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
